import React from "react";
import { motion, useSpring, useTransform } from "framer-motion";

/**
 * ThermalCore — HVAC "Business Pulse" visual (LIGHT theme).
 *
 * Two concentric rings on a warm paper backdrop:
 *   • Outer ring = LOAD  (expenses + open POs + overdue AR)
 *   • Inner ring = FLOW  (revenue + net profit)
 *
 * Ratio drives the state readout:
 *   0 – 0.45 → OPTIMAL     (teal)
 *   0.45 – 0.65 → HOLDING  (amber)
 *   0.65 – 0.85 → WARM     (orange)
 *   0.85 – 1.00 → OVERHEATING (red)
 */
export default function ThermalCore({ heatLoad = 0, coolingCapacity = 0, marginPct = 0, testid = "hero-thermal-core" }) {
  const total = Math.max(1, heatLoad + coolingCapacity);
  const heatRatio = Math.min(1, heatLoad / total);
  const coolRatio = 1 - heatRatio;
  const state = classifyState(heatRatio);

  // Ring geometry (SVG viewbox 400×400)
  const outerR = 175;
  const midR   = 140;
  const coreR  = 105;
  const outerCirc = 2 * Math.PI * outerR;
  const midCirc   = 2 * Math.PI * midR;

  const heatOffset = outerCirc * (1 - heatRatio);
  const coolOffset = midCirc * (1 - coolRatio);

  // Animated margin counter
  const spring = useSpring(0, { stiffness: 60, damping: 15 });
  const rounded = useTransform(spring, (v) => `${Math.round(v)}`);
  React.useEffect(() => { spring.set(marginPct); }, [marginPct, spring]);

  return (
    <div className="relative aspect-square w-full max-w-[520px] mx-auto" data-testid={testid}>
      {/* Warm ambient glow */}
      <div
        className="absolute inset-0 rounded-full pointer-events-none tc-breathe"
        style={{
          background: `radial-gradient(circle at center, ${state.glow} 0%, transparent 62%)`,
          filter: "blur(28px)",
        }}
      />

      <svg viewBox="0 0 400 400" className="relative w-full h-full">
        <defs>
          <linearGradient id="tc-load" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"  stopColor="#F97316" />
            <stop offset="100%" stopColor="#EA580C" />
          </linearGradient>
          <linearGradient id="tc-flow" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0891B2" />
            <stop offset="100%" stopColor="#0EA5E9" />
          </linearGradient>
          <radialGradient id="tc-core-fill">
            <stop offset="0%"  stopColor="#FFFDFA" />
            <stop offset="100%" stopColor="#F5EFE6" />
          </radialGradient>
          <filter id="tc-soft" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="2" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Tick marks — 60 hair-thin marks on a soft dial */}
        <g className="tc-spin-rev" opacity="0.35" style={{ transformOrigin: '200px 200px' }}>
          {Array.from({ length: 60 }).map((_, i) => {
            const a = (i * 6 - 90) * (Math.PI / 180);
            const r1 = 192, r2 = i % 5 === 0 ? 178 : 184;
            const x1 = 200 + r1 * Math.cos(a);
            const y1 = 200 + r1 * Math.sin(a);
            const x2 = 200 + r2 * Math.cos(a);
            const y2 = 200 + r2 * Math.sin(a);
            return <line key={`tk-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#D6D3D1" strokeWidth={i % 5 === 0 ? 1.5 : 0.6} />;
          })}
        </g>

        {/* Outer track (LOAD) */}
        <circle cx="200" cy="200" r={outerR} fill="none" stroke="#EDE7DD" strokeWidth="12" />
        <circle cx="200" cy="200" r={outerR}
          fill="none"
          stroke="url(#tc-load)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={outerCirc}
          strokeDashoffset={heatOffset}
          transform="rotate(-90 200 200)"
          filter="url(#tc-soft)"
        />

        {/* Middle track (FLOW) */}
        <circle cx="200" cy="200" r={midR} fill="none" stroke="#EDE7DD" strokeWidth="7" />
        <circle cx="200" cy="200" r={midR}
          fill="none"
          stroke="url(#tc-flow)"
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={midCirc}
          strokeDashoffset={coolOffset}
          transform="rotate(-90 200 200)"
          filter="url(#tc-soft)"
        />

        {/* Rotating airflow curves — HVAC signature */}
        <g className="tc-spin-slow" opacity="0.28" style={{ transformOrigin: '200px 200px' }}>
          {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
            <path key={deg}
              d={`M 200 ${200 - coreR - 3} Q 214 ${200 - coreR + 15} 200 ${200 - coreR + 34}`}
              stroke="#A8A29E" strokeWidth="1.2" fill="none" strokeLinecap="round"
              transform={`rotate(${deg} 200 200)`}
            />
          ))}
        </g>

        {/* Core disc */}
        <circle cx="200" cy="200" r={coreR} fill="url(#tc-core-fill)" stroke="#EDE7DD" strokeWidth="1.5" />

        {/* State readout — SVG for crisp letter spacing */}
        <text x="200" y="176" textAnchor="middle"
          className="font-tc-mono"
          fill="#A8A29E" fontSize="10" letterSpacing="4">SYSTEM STATE</text>
        <text x="200" y="206" textAnchor="middle"
          className="font-tc-display"
          fill={state.color} fontSize="22" fontWeight="800" letterSpacing="1">{state.label}</text>
      </svg>

      {/* Overlay: margin count-up */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none translate-y-9">
        <div className="text-[10px] font-tc-mono tracking-[0.32em] text-stone-500">MARGIN</div>
        <motion.div
          className="font-tc-display font-black text-stone-900 text-[34px] leading-none mt-1 flex items-baseline"
          data-testid={`${testid}-margin`}
        >
          <motion.span>{rounded}</motion.span>
          <span className="text-stone-400 text-[22px] ml-0.5">%</span>
        </motion.div>
      </div>

      {/* Bottom legend chips */}
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex gap-3 text-[10px] font-tc-mono tracking-[0.14em]">
        <div className="flex items-center gap-1.5 bg-white/80 backdrop-blur border border-stone-200 rounded-full px-2.5 py-1 shadow-sm">
          <span className="w-2 h-2 rounded-full" style={{ background: "linear-gradient(135deg,#F97316,#EA580C)" }} />
          <span className="text-stone-600">LOAD</span>
          <span className="text-stone-900 font-semibold">{Math.round(heatRatio * 100)}%</span>
        </div>
        <div className="flex items-center gap-1.5 bg-white/80 backdrop-blur border border-stone-200 rounded-full px-2.5 py-1 shadow-sm">
          <span className="w-2 h-2 rounded-full" style={{ background: "linear-gradient(135deg,#0891B2,#0EA5E9)" }} />
          <span className="text-stone-600">FLOW</span>
          <span className="text-stone-900 font-semibold">{Math.round(coolRatio * 100)}%</span>
        </div>
      </div>
    </div>
  );
}

function classifyState(heatRatio) {
  if (heatRatio < 0.45) return { label: "OPTIMAL",     color: "#0891B2", glow: "rgba(8,145,178,0.18)" };
  if (heatRatio < 0.65) return { label: "HOLDING",     color: "#B45309", glow: "rgba(180,83,9,0.16)" };
  if (heatRatio < 0.85) return { label: "WARM",        color: "#EA580C", glow: "rgba(234,88,12,0.24)" };
  return                        { label: "OVERHEATING", color: "#DC2626", glow: "rgba(220,38,38,0.30)" };
}
